import type { DeliveryState, RunRecord } from '../../shared/types';
import { STALE_AFTER_SEC } from '../../shared/constants';

/** Shown when the hourly job has not completed recently: the monitor itself is the first thing to monitor. */
export function WatchdogBanner({ lastRun, now }: { lastRun: RunRecord | null; now: number }) {
  if (lastRun && lastRun.finishedAt && now - lastRun.finishedAt < STALE_AFTER_SEC && !lastRun.error) return null;
  const msg = !lastRun
    ? 'No job run recorded yet. Trigger one with "Run now" or wait for the hourly cron.'
    : lastRun.error
      ? `Last run failed: ${lastRun.error}`
      : `Last run finished ${Math.round((now - (lastRun.finishedAt ?? lastRun.startedAt)) / 3600)} h ago. The cron may be stalled.`;
  return <div class="banner banner--critical" role="alert">{msg}</div>;
}

const CHANNEL: Record<'matrix' | 'pushover', { name: string; where: string; fix: string }> = {
  matrix: { name: 'Matrix', where: 'the room', fix: 'fix the token or homeserver' },
  pushover: { name: 'Pushover', where: 'your phone', fix: 'fix the user key or app token' },
};

/** One banner per channel whose last delivery was refused with nothing gone through since. */
export function DeliveryBanner({ delivery, now }: { delivery: DeliveryState | null; now: number }) {
  if (!delivery) return null;
  return (
    <>
      {(['matrix', 'pushover'] as const).map((key) => {
        const c = delivery[key];
        if (!c || c.lastFailAt === null || (c.lastOkAt !== null && c.lastOkAt >= c.lastFailAt)) return null;
        const ago = Math.max(1, Math.round((now - c.lastFailAt) / 60));
        const { name, where, fix } = CHANNEL[key];
        return (
          <div class="banner banner--critical" role="alert" key={key}>
            {name} refused the last notice {ago < 90 ? `${ago} min` : `${Math.round(ago / 60)} h`} ago ({c.lastError ?? 'unknown error'}).
            Alerts are not reaching {where}; {fix}, then use Test notifications to clear this.
          </div>
        );
      })}
    </>
  );
}
